import { defineStore } from 'pinia';
import { useImageAssetStore } from './useImageAssetStore';
import { useImageAnalysisStore } from './useImageAnalysisStore';
import {
    createEmptyFinalResultImageState,
    normalizeSerializablePalette
} from './imageStoreShared';
import { normalizeAnalysisPayload } from '../analysis/subject/analysisPayloadMapper';

const normalizeCacheString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const hasAiAnalysisDetailFields = (payload = {}) => Boolean(
    payload.executionId ||
    payload.jobId ||
    payload.status ||
    payload.analysisMarkdown ||
    payload.analysisPayload ||
    payload.meta ||
    payload.retryable === true ||
    payload.retryWithSameRequestId === true ||
    payload.failureCode ||
    payload.recoveryMode ||
    payload.requestable === true ||
    payload.nextAction ||
    payload.aiDispatchState ||
    payload.aiResultId ||
    payload.outputLanguage
);

const isCompletedAiAnalysisDetail = (payload = {}) => payload?.status === 'completed';

const deepSanitizeForClone = (value) => {
    if (value === null || typeof value === 'undefined') return null;
    if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(deepSanitizeForClone);
    if (isPlainObject(value)) {
        const result = {};
        for (const key of Object.keys(value)) {
            const sanitized = deepSanitizeForClone(value[key]);
            if (sanitized !== null) {
                result[key] = sanitized;
            }
        }
        return result;
    }
    return null;
};

const normalizeAiAnalysisDetailCachePayload = (payload = null) => {
    const subjectId = normalizeCacheString(payload?.subjectId || payload?.subject_id);
    const executionId = normalizeCacheString(payload?.executionId || payload?.execution_id);
    const jobId = normalizeCacheString(payload?.jobId || payload?.job_id || payload?.id);
    const status = normalizeCacheString(payload?.status);
    const analysisMarkdown = typeof payload?.analysisMarkdown === 'string'
        ? (payload.analysisMarkdown.trim() || null)
        : (typeof payload?.analysis_markdown === 'string' ? (payload.analysis_markdown.trim() || null) : null);
    const analysisPayload = normalizeAnalysisPayload(payload?.analysisPayload || payload?.analysis_payload);
    const outputLanguage = normalizeCacheString(payload?.outputLanguage || payload?.output_language || payload?.meta?.output_language || payload?.meta?.outputLanguage);
    const meta = deepSanitizeForClone(isPlainObject(payload?.meta) ? payload.meta : null);
    const retryable = hasOwn(payload, 'retryable') ? payload.retryable === true : undefined;
    const retryWithSameRequestId = hasOwn(payload, 'retryWithSameRequestId') || hasOwn(payload, 'retry_with_same_request_id')
        ? (payload?.retryWithSameRequestId === true || payload?.retry_with_same_request_id === true)
        : undefined;
    const failureCode = normalizeCacheString(payload?.failureCode || payload?.failure_code);
    const recoveryMode = normalizeCacheString(payload?.recoveryMode || payload?.recovery_mode);
    const requestable = hasOwn(payload, 'requestable') ? payload.requestable === true : undefined;
    const nextAction = deepSanitizeForClone(isPlainObject(payload?.nextAction)
        ? payload.nextAction
        : (isPlainObject(payload?.next_action) ? payload.next_action : null));
    const aiDispatchState = normalizeCacheString(payload?.aiDispatchState || payload?.ai_dispatch_state);
    const aiResultId = normalizeCacheString(payload?.aiResultId || payload?.ai_result_id);
    const normalizedPayload = {
        subjectId,
        executionId,
        jobId,
        status,
        analysisMarkdown,
        analysisPayload: deepSanitizeForClone(analysisPayload),
        outputLanguage,
        meta,
        retryable,
        retryWithSameRequestId,
        failureCode,
        recoveryMode,
        requestable,
        nextAction,
        aiDispatchState,
        aiResultId,
        updatedAt: payload?.updatedAt || payload?.timestamp || Date.now(),
        source: normalizeCacheString(payload?.source) || 'memory'
    };

    const hasReadableResult = Boolean(normalizedPayload.analysisPayload || normalizedPayload.analysisMarkdown);

    if (!subjectId || !hasAiAnalysisDetailFields(normalizedPayload) || !isCompletedAiAnalysisDetail(normalizedPayload) || !hasReadableResult) {
        return null;
    }

    return normalizedPayload;
};

export const useResultMediaStore = defineStore('resultMedia', {
    state: () => ({
        referencePreset: null,
        analysisTaskContext: null,
        referenceAnalysisResult: null,
        comparisonSummary: null,
        comparisonStatus: 'idle',
        comparisonError: null,
        referenceEvidenceStatus: 'idle',
        aiAnalysisDetailCache: null,
        finalResultImage: createEmptyFinalResultImageState()
    }),

    actions: {
        async ensureDb() {
            const assetStore = useImageAssetStore();
            if (!assetStore.db) {
                await assetStore.initDB();
            }
            return assetStore.db;
        },

        setReferencePreset(preset) {
            this.referencePreset = preset || null;
        },

        setAnalysisTaskContext(context) {
            this.analysisTaskContext = context || null;
        },

        clearAnalysisTaskContext() {
            const analysisStore = useImageAnalysisStore();
            this.referencePreset = null;
            this.analysisTaskContext = null;
            this.clearComparisonState();
            analysisStore.resetAnalysisBootstrapState();
        },

        resetResultMediaState() {
            this.referencePreset = null;
            this.analysisTaskContext = null;
            this.referenceAnalysisResult = null;
            this.comparisonSummary = null;
            this.comparisonStatus = 'idle';
            this.comparisonError = null;
            this.referenceEvidenceStatus = 'idle';
            this.aiAnalysisDetailCache = null;
            this.finalResultImage = createEmptyFinalResultImageState();
        },

        setReferenceAnalysisCore(result) {
            const analysisStore = useImageAnalysisStore();
            if (!result) {
                this.referenceAnalysisResult = null;
                analysisStore.clearReferenceArtifacts();
                return;
            }

            this.referenceAnalysisResult = {
                ...(this.referenceAnalysisResult || {}),
                ...result,
                core: result.core || null,
                evidence: {
                    available: Boolean(result?.evidence?.available),
                    loaded: Boolean(result?.evidence?.loaded),
                    ...(this.referenceAnalysisResult?.evidence || {}),
                    ...(result?.evidence || {})
                }
            };

            analysisStore.setReferenceCoreMirror(this.referenceAnalysisResult?.core || null);
            analysisStore.hydrateReferenceMaskArtifacts({
                core: result.core || null,
                evidence: result.evidence || null
            });
        },

        setReferenceAnalysisEvidence(evidence) {
            const analysisStore = useImageAnalysisStore();
            const current = this.referenceAnalysisResult || {};
            this.referenceAnalysisResult = {
                ...current,
                evidence: {
                    available: true,
                    loaded: true,
                    ...(current.evidence || {}),
                    ...(evidence || {})
                }
            };

            analysisStore.setReferenceCoreMirror(current.core || null);
            analysisStore.hydrateReferenceMaskArtifacts({
                core: current.core || null,
                evidence: evidence || null
            });
        },

        setComparisonSummary(summary) {
            this.comparisonSummary = summary || null;
        },

        setComparisonStatus(status, error = null) {
            this.comparisonStatus = status;
            this.comparisonError = error;
        },

        setReferenceEvidenceStatus(status, error = null) {
            this.referenceEvidenceStatus = status;
            if (status === 'error' && error) {
                this.comparisonError = error;
            }
        },

        clearComparisonState() {
            const analysisStore = useImageAnalysisStore();
            this.referenceAnalysisResult = null;
            this.comparisonSummary = null;
            this.comparisonStatus = 'idle';
            this.comparisonError = null;
            this.referenceEvidenceStatus = 'idle';
            this.aiAnalysisDetailCache = null;
            analysisStore.clearReferenceArtifacts();
        },

        getReferenceAnalysisStoreName(profile = 'core') {
            return profile === 'evidence' ? 'referenceAnalysisEvidence' : 'referenceAnalysisCore';
        },

        getReferenceImageCacheId(cacheKey) {
            if (!cacheKey) {
                return '';
            }
            return `reference-image::${cacheKey}`;
        },

        buildFinalResultCacheId(scopeKey) {
            const normalizedScopeKey = String(scopeKey || '').trim();
            if (!normalizedScopeKey) {
                return '';
            }
            return `final-result::${normalizedScopeKey}`;
        },

        buildAiAnalysisDetailCacheId(subjectId) {
            const normalizedSubjectId = String(subjectId || '').trim();
            if (!normalizedSubjectId) {
                return '';
            }
            return `ai-analysis-detail::${normalizedSubjectId}`;
        },

        setAiAnalysisDetailCache(payload = null) {
            const normalizedPayload = normalizeAiAnalysisDetailCachePayload(payload);

            if (!normalizedPayload) {
                this.aiAnalysisDetailCache = null;
                return;
            }

            this.aiAnalysisDetailCache = normalizedPayload;
        },

        async clearAiAnalysisDetailCache({ subjectId } = {}) {
            const normalizedSubjectId = String(subjectId || '').trim();
            const currentSubjectId = String(this.aiAnalysisDetailCache?.subjectId || '').trim();
            const shouldClearMemory = !normalizedSubjectId || !currentSubjectId || currentSubjectId === normalizedSubjectId;

            if (shouldClearMemory) {
                this.aiAnalysisDetailCache = null;
            }

            if (!normalizedSubjectId) {
                return;
            }

            try {
                const db = await this.ensureDb();
                const cacheId = this.buildAiAnalysisDetailCacheId(normalizedSubjectId);
                if (!cacheId) {
                    return;
                }

                await db.delete('images', cacheId);
                console.log('[ResultMediaStore] AI analysis detail cache cleared', {
                    subjectId: normalizedSubjectId
                });
            } catch (error) {
                console.warn('[ResultMediaStore] 清理 AI analysis detail 缓存失败:', error);
            }
        },

        async getAiAnalysisDetailCache({ subjectId } = {}) {
            const normalizedSubjectId = String(subjectId || '').trim();
            if (!normalizedSubjectId) {
                console.log('[ResultMediaStore] AI analysis detail cache read skipped: missing subjectId');
                return null;
            }

            try {
                const db = await this.ensureDb();
                const cacheId = this.buildAiAnalysisDetailCacheId(normalizedSubjectId);
                if (!cacheId) {
                    console.log('[ResultMediaStore] AI analysis detail cache read skipped: invalid cacheId', {
                        subjectId: normalizedSubjectId
                    });
                    return null;
                }

                const record = await db.get('images', cacheId);
                const normalizedRecord = normalizeAiAnalysisDetailCachePayload({
                    ...(record || {}),
                    subjectId: normalizedSubjectId,
                    updatedAt: record?.timestamp || null,
                    source: record?.source || null
                });

                console.log('[ResultMediaStore] AI analysis detail cache read', {
                    subjectId: normalizedSubjectId,
                    hit: Boolean(normalizedRecord),
                    updatedAt: record?.timestamp || null,
                    source: record?.source || null
                });

                return normalizedRecord;
            } catch (error) {
                console.warn('[ResultMediaStore] 读取 AI analysis detail 缓存失败:', error);
                return null;
            }
        },

        async saveAiAnalysisDetailCache(payload = {}) {
            const normalizedPayload = normalizeAiAnalysisDetailCachePayload(payload);
            const normalizedSubjectId = normalizeCacheString(payload?.subjectId || payload?.subject_id);
            const normalizedAnalysisMarkdown = typeof payload?.analysisMarkdown === 'string'
                ? (payload.analysisMarkdown.trim() || null)
                : (typeof payload?.analysis_markdown === 'string' ? (payload.analysis_markdown.trim() || null) : null);
            const normalizedAnalysisPayload = normalizeAnalysisPayload(payload?.analysisPayload || payload?.analysis_payload);
            const hasDetailFields = Boolean(
                normalizeCacheString(payload?.executionId || payload?.execution_id)
                || normalizeCacheString(payload?.jobId || payload?.job_id || payload?.id)
                || normalizeCacheString(payload?.status)
                || normalizedAnalysisMarkdown
                || normalizedAnalysisPayload
                || normalizeCacheString(payload?.outputLanguage || payload?.output_language || payload?.meta?.output_language || payload?.meta?.outputLanguage)
                || isPlainObject(payload?.meta)
                || payload?.retryable === true
                || payload?.retryWithSameRequestId === true
                || payload?.retry_with_same_request_id === true
                || normalizeCacheString(payload?.failureCode || payload?.failure_code)
                || normalizeCacheString(payload?.recoveryMode || payload?.recovery_mode)
                || payload?.requestable === true
                || isPlainObject(payload?.nextAction)
                || isPlainObject(payload?.next_action)
                || normalizeCacheString(payload?.aiDispatchState || payload?.ai_dispatch_state)
                || normalizeCacheString(payload?.aiResultId || payload?.ai_result_id)
            );

            if (!normalizedPayload) {
                console.log('[ResultMediaStore] AI analysis detail cache save skipped', {
                    hasSubjectId: Boolean(normalizedSubjectId),
                    hasDetailFields,
                    status: normalizeCacheString(payload?.status),
                    source: payload?.source || 'unknown'
                });
                return;
            }

            this.setAiAnalysisDetailCache(normalizedPayload);

            try {
                const db = await this.ensureDb();
                console.log('[ResultMediaStore] ensureDb result', { hasDb: !!db });
                const cacheId = this.buildAiAnalysisDetailCacheId(normalizedPayload.subjectId);
                console.log('[ResultMediaStore] building cacheId', { subjectId: normalizedPayload.subjectId, cacheId });
                if (!cacheId) {
                    console.warn('[ResultMediaStore] cacheId is empty, aborting save');
                    return;
                }

                const recordToSave = {
                    id: cacheId,
                    subjectId: normalizedPayload.subjectId,
                    executionId: normalizedPayload.executionId,
                    jobId: normalizedPayload.jobId,
                    status: normalizedPayload.status,
                    analysisMarkdown: normalizedPayload.analysisMarkdown,
                    analysisPayload: normalizedPayload.analysisPayload,
                    outputLanguage: normalizedPayload.outputLanguage,
                    meta: normalizedPayload.meta,
                    retryable: normalizedPayload.retryable,
                    retryWithSameRequestId: normalizedPayload.retryWithSameRequestId,
                    failureCode: normalizedPayload.failureCode,
                    recoveryMode: normalizedPayload.recoveryMode,
                    requestable: normalizedPayload.requestable,
                    nextAction: normalizedPayload.nextAction,
                    aiDispatchState: normalizedPayload.aiDispatchState,
                    aiResultId: normalizedPayload.aiResultId,
                    source: normalizedPayload.source,
                    timestamp: Date.now()
                };
                console.log('[ResultMediaStore] about to save to IndexedDB', { cacheId, record: recordToSave });

                await db.put('images', recordToSave);
                console.log('[ResultMediaStore] AI analysis detail cache saved', {
                    subjectId: normalizedPayload.subjectId,
                    source: normalizedPayload.source,
                    status: normalizedPayload.status,
                    hasAnalysisMarkdown: Boolean(normalizedPayload.analysisMarkdown),
                    hasAnalysisPayload: Boolean(normalizedPayload.analysisPayload),
                    jobId: normalizedPayload.jobId
                });
            } catch (error) {
                console.warn('[ResultMediaStore] 保存 AI analysis detail 缓存失败:', error);
            }
        },

        async hydrateAiAnalysisDetailCache({ subjectId } = {}) {
            const normalizedSubjectId = String(subjectId || '').trim();
            if (!normalizedSubjectId) {
                this.aiAnalysisDetailCache = null;
                console.log('[ResultMediaStore] AI analysis detail hydrate skipped: missing subjectId');
                return null;
            }

            const cached = await this.getAiAnalysisDetailCache({ subjectId: normalizedSubjectId });
            if (!cached) {
                this.aiAnalysisDetailCache = null;
                console.log('[ResultMediaStore] AI analysis detail hydrate miss', {
                    subjectId: normalizedSubjectId
                });
                return null;
            }

            this.setAiAnalysisDetailCache({
                ...cached,
                source: cached.source || 'idb'
            });
            console.log('[ResultMediaStore] AI analysis detail hydrated into memory', {
                subjectId: normalizedSubjectId,
                source: cached.source || 'idb',
                updatedAt: cached.updatedAt || null
            });
            return this.aiAnalysisDetailCache;
        },

        setFinalResultImage(payload = {}) {
            const scopeKey = String(payload.scopeKey || '').trim();
            if (!scopeKey || !payload.imageData) {
                return;
            }

            this.finalResultImage = {
                scopeKey,
                imageData: payload.imageData,
                hashId: payload.hashId || null,
                type: payload.type || payload.mimeType || 'image/png',
                fileName: payload.fileName || '',
                updatedAt: payload.updatedAt || Date.now(),
                colorPalette: payload.colorPalette || null,
                beforePalette: payload.beforePalette || null
            };
        },

        async clearFinalResultImage({ scopeKey } = {}) {
            const normalizedScopeKey = String(scopeKey || '').trim();
            const currentScopeKey = String(this.finalResultImage?.scopeKey || '').trim();
            const shouldClearMemory = !normalizedScopeKey || !currentScopeKey || currentScopeKey === normalizedScopeKey;

            if (shouldClearMemory) {
                this.finalResultImage = createEmptyFinalResultImageState();
            }

            if (!normalizedScopeKey) {
                return;
            }

            try {
                const db = await this.ensureDb();
                const cacheId = this.buildFinalResultCacheId(normalizedScopeKey);
                if (cacheId) {
                    await db.delete('images', cacheId);
                }
            } catch (error) {
                console.warn('[IndexedDB] 清理完成图缓存失败:', error);
            }
        },

        async getFinalResultImageCache({ scopeKey } = {}) {
            const normalizedScopeKey = String(scopeKey || '').trim();
            if (!normalizedScopeKey) {
                return null;
            }

            try {
                const db = await this.ensureDb();
                const cacheId = this.buildFinalResultCacheId(normalizedScopeKey);
                if (!cacheId) {
                    return null;
                }

                const record = await db.get('images', cacheId);
                if (!record?.data) {
                    return null;
                }

                return {
                    scopeKey: normalizedScopeKey,
                    imageData: record.data,
                    type: record.type || 'image/png',
                    fileName: record.fileName || '',
                    hashId: record.hashId || null,
                    updatedAt: record.timestamp || null,
                    colorPalette: record.colorPalette || null,
                    beforePalette: record.beforePalette || null
                };
            } catch (error) {
                console.warn('[IndexedDB] 读取完成图缓存失败:', error);
                return null;
            }
        },

        async saveFinalResultImageCache({ scopeKey, payload } = {}) {
            const normalizedScopeKey = String(scopeKey || '').trim();
            if (!normalizedScopeKey || !payload?.imageData) {
                return;
            }

            try {
                const db = await this.ensureDb();
                const cacheId = this.buildFinalResultCacheId(normalizedScopeKey);
                if (!cacheId) {
                    return;
                }

                await db.put('images', {
                    id: cacheId,
                    data: payload.imageData,
                    type: payload.type || payload.mimeType || 'image/png',
                    fileName: payload.fileName || '',
                    hashId: payload.hashId || null,
                    colorPalette: payload.colorPalette || null,
                    beforePalette: normalizeSerializablePalette(payload.beforePalette),
                    timestamp: Date.now()
                });
            } catch (error) {
                console.warn('[IndexedDB] 保存完成图缓存失败:', error);
            }
        },

        async hydrateFinalResultImage({ scopeKey } = {}) {
            const normalizedScopeKey = String(scopeKey || '').trim();
            if (!normalizedScopeKey) {
                this.finalResultImage = createEmptyFinalResultImageState();
                return null;
            }

            const cached = await this.getFinalResultImageCache({ scopeKey: normalizedScopeKey });
            if (!cached?.imageData) {
                this.finalResultImage = createEmptyFinalResultImageState();
                return null;
            }

            this.setFinalResultImage({
                scopeKey: normalizedScopeKey,
                imageData: cached.imageData,
                hashId: cached.hashId,
                type: cached.type,
                fileName: cached.fileName,
                updatedAt: cached.updatedAt || Date.now(),
                colorPalette: cached.colorPalette || null,
                beforePalette: cached.beforePalette || null
            });

            return this.finalResultImage;
        },

        async getReferenceImageCache(cacheKey) {
            if (!cacheKey) {
                return null;
            }

            try {
                const db = await this.ensureDb();
                const id = this.getReferenceImageCacheId(cacheKey);
                if (!id) {
                    return null;
                }

                const record = await db.get('images', id);
                if (!record || !record.data) {
                    console.log('[ResultMediaStore] 参考图图片缓存未命中', {
                        cacheKey,
                        id
                    });
                    return null;
                }

                console.log('[ResultMediaStore] 参考图图片缓存读取命中', {
                    cacheKey,
                    id,
                    timestamp: record.timestamp || 0,
                    dataType: typeof record.data,
                    dataLength: record.data?.length || 0,
                    isDataUrl: typeof record.data === 'string' && record.data.startsWith('data:')
                });
                return {
                    data: record.data,
                    type: record.type || 'image/png',
                    timestamp: record.timestamp || 0
                };
            } catch (error) {
                console.warn('[IndexedDB] 读取参考图图片缓存失败', {
                    cacheKey,
                    errorName: error?.name || 'UnknownError',
                    errorMessage: error?.message || error
                });
                return null;
            }
        },

        async saveReferenceImageCache(cacheKey, payload) {
            if (!cacheKey || !payload?.data) {
                console.log('[ResultMediaStore] saveReferenceImageCache 跳过：缺少 cacheKey 或 data', {
                    hasCacheKey: !!cacheKey,
                    hasData: !!payload?.data
                });
                return;
            }

            try {
                const db = await this.ensureDb();
                const id = this.getReferenceImageCacheId(cacheKey);
                if (!id) {
                    console.log('[ResultMediaStore] saveReferenceImageCache 跳过：无法生成 id', { cacheKey: cacheKey?.substring(0, 80) + '...' });
                    return;
                }

                const startWrite = performance.now();
                await db.put('images', {
                    id,
                    data: payload.data,
                    type: payload.type || 'image/png',
                    timestamp: Date.now()
                });
                const writeDuration = Math.round(performance.now() - startWrite);

                console.log('[ResultMediaStore] 参考图图片缓存保存成功', {
                    cacheKey,
                    id: id?.substring(0, 60) + '...',
                    dataLength: payload.data?.length || 0,
                    type: payload.type || 'image/png',
                    writeDurationMs: writeDuration
                });
            } catch (error) {
                console.warn('[ResultMediaStore] 保存参考图图片缓存失败', {
                    cacheKey,
                    errorName: error?.name || 'UnknownError',
                    errorMessage: error?.message || error
                });
            }
        },

        async getReferenceAnalysisCache(profile, cacheKey) {
            if (!cacheKey) {
                return null;
            }

            try {
                const db = await this.ensureDb();
                const storeName = this.getReferenceAnalysisStoreName(profile);
                return await db.get(storeName, cacheKey);
            } catch (error) {
                console.warn('[IndexedDB] 读取参考图分析缓存失败:', error);
                return null;
            }
        },

        async saveReferenceAnalysisCache(profile, payload) {
            if (!payload?.cacheKey) {
                return;
            }

            try {
                const db = await this.ensureDb();
                const storeName = this.getReferenceAnalysisStoreName(profile);
                await db.put(storeName, payload);
            } catch (error) {
                console.warn('[IndexedDB] 保存参考图分析缓存失败:', error);
            }
        },

        // Step E.5：清理 Step D 之前写入、仍含逐像素 Mask 的旧 reference analysis 缓存。
        // 仅删除 profile∈{core,evidence} 且 version!==targetVersion 的记录；不含像素 Mask 的 reference image 缓存不在此清理。
        async deleteReferenceAnalysisCacheByVersion(targetVersion) {
            if (!targetVersion) {
                console.warn('[ResultMediaStore] deleteReferenceAnalysisCacheByVersion 跳过：缺少 targetVersion');
                return;
            }
            try {
                const db = await this.ensureDb();
                let totalRemoved = 0;
                for (const profile of ['core', 'evidence']) {
                    const storeName = this.getReferenceAnalysisStoreName(profile);
                    const all = await db.getAll(storeName);
                    let removed = 0;
                    for (const rec of all) {
                        if (!rec || !rec.cacheKey) {
                            continue;
                        }
                        if (rec.version !== targetVersion) {
                            await db.delete(storeName, rec.cacheKey);
                            removed += 1;
                        }
                    }
                    totalRemoved += removed;
                    console.log(`[ResultMediaStore] 清理旧 reference analysis 缓存(${profile})`, { targetVersion, removed });
                }
                if (totalRemoved > 0) {
                    console.log('[ResultMediaStore] 已清理旧 reference analysis 缓存', { targetVersion, totalRemoved });
                }
            } catch (error) {
                console.warn('[ResultMediaStore] 清理旧 reference analysis 缓存失败', error);
            }
        }
    }
});
